import { SvgIcon } from "@mui/material";
import { useCallback, useMemo } from "react";

import {
  Business,
  Dataset,
  Highlight,
  Radar,
  SupervisedUserCircle,
  SupportAgent,
  WifiTethering,
  PrecisionManufacturing,
  Assessment,
  LocalShipping,
  SettingsInputAntenna,

} from "@mui/icons-material";
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import {
  ChartBarIcon,
  AdjustmentsHorizontalIcon,
  CalculatorIcon,
  CalendarDaysIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  CubeIcon,
  CurrencyDollarIcon,
  GlobeAltIcon,
  PresentationChartLineIcon,
  QueueListIcon,
  ShoppingCartIcon,
  Squares2X2Icon,
  SquaresPlusIcon,
  UserGroupIcon,
  UserPlusIcon,
  WalletIcon,
  WrenchIcon,
  FunnelIcon,
  MegaphoneIcon,
  NumberedListIcon
} from "@heroicons/react/24/solid";

import { SystemModulesEnum } from "@/auth/interfaces/system-modules.enum";
import { PermissionsEnum } from "@/auth/interfaces/permissions.enum";
import { hasPermission } from "@/shared/utils/general/has-permission.utils";
import { ROUTER_PATHS } from "@/shared/constants/routes/router-paths.constants";
import { hasSystemModule } from "@/shared/utils/general/has-module.utils";
import { NestedMenuItemLevels } from "./nested-items.enum";



export type NestedMenuItem = {
  title: string;
  icon?: React.ReactNode;
  to?: string;

  level?: number;
  items?: NestedMenuItem[];

  admin?: boolean;
};

export const useNestedMenu = () => {
  ///* helpers ------------
  const renderByPermission = useCallback(
    (
      permission: PermissionsEnum,
      item: NestedMenuItem,
    ): NestedMenuItem | null => {
      return hasPermission(permission) ? item : null;
    },
    [],
  );

  // ======================================
  const renderByModule = useCallback(
    (
      module: SystemModulesEnum,
      item: NestedMenuItem,
    ): NestedMenuItem | null => {
      return hasSystemModule(module) ? item : null;
    },
    [],
  );

  const cleanEmptyItems = (menu: NestedMenuItem[]): NestedMenuItem[] => {
    return menu
      .map((item) => {
        if (item?.items && item?.items.length > 0) {
          item.items = cleanEmptyItems(item?.items);

          // if the item has no children, remove it (actual object)
          if (item?.items?.length === 0) {
            return null;
          }
        }

        // Si el item no tiene 'items' o tiene items válidos, es un item válido
        if (!item?.items || item?.items?.length > 0) {
          return item;
        }

        return null;
      })
      .filter((item): item is NestedMenuItem => item !== null);
  };

  const nestedMenuItesm: NestedMenuItem[] = useMemo(() => {
    return [
      ///* Administración --------------------------------
      {
        title: "Administración",
        icon: (
          <SvgIcon fontSize="small">
            <Squares2X2Icon />
          </SvgIcon>
        ),
        level: NestedMenuItemLevels.firstLevelWithChildren,
        items: [
          // hide this item if user doesn't have permission (child)
          renderByModule(SystemModulesEnum.ACTIVACIONES_RAD_CHECK, {
            title: "RAD CHECK",
            to: ROUTER_PATHS.administration.radCheckNav,
            level: 2,
          }),
          // hide this item if user doesn't have permission (child)
          renderByModule(SystemModulesEnum.ADMIN_IVA, {
            title: "IVA",
            to: ROUTER_PATHS.administration.ivaNav,
            level: 2,
          }),

          renderByModule(SystemModulesEnum.ADMIN_RESPONSABLE, {
            title: "Responsable",
            to: ROUTER_PATHS.administration.responsableNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.ADMIN_OBSERVACION, {
            title: "Observacion",
            to: ROUTER_PATHS.administration.observacionNav,
            level: 2,
          }),

          renderByModule(SystemModulesEnum.ADMIN_EMPRESAS, {
            title: "Empresas",
            to: ROUTER_PATHS.administration.empresaNav,
            level: 2,
          }),

          renderByModule(SystemModulesEnum.ADMIN_METODO_PAGO, {
            title: "Métodos de Pago",
            to: ROUTER_PATHS.administration.metodoPagoNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.ADMIN_METODO_PAGO_PROVEEDOR, {
            title: "Métodos de Pago Proveedor",
            to: ROUTER_PATHS.administration.metodoPagoProveedorNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.ADMIN_PROMOCIONES, {
            title: "Promociones",
            to: ROUTER_PATHS.administration.promocionesNav,
            level: 2,
          }),

          renderByModule(SystemModulesEnum.ADMIN_PAISES, {
            title: "Países",
            to: ROUTER_PATHS.administration.paisesNav,
            level: 2,
          }),

          renderByModule(SystemModulesEnum.ADMIN_PROVINCIAS, {
            title: "Provincias",
            to: ROUTER_PATHS.administration.provinciasNav,
            level: 2,
          }),

          renderByModule(SystemModulesEnum.ADMIN_CIUDADES, {
            title: "Ciudades",
            to: ROUTER_PATHS.administration.ciudadesNav,
            level: 2,
          }),

          renderByModule(SystemModulesEnum.ADMIN_TIPO_INSTALACION, {
            title: "Tipo de Instalación",
            to: ROUTER_PATHS.administration.tiposInstalacionNav,
            level: 2,
          }),

          renderByModule(SystemModulesEnum.ADMIN_TIPO_COMPROBANTE, {
            title: "Tipo de Comprobante",
            to: ROUTER_PATHS.administration.tipoComprobanteNav,
            level: 2,
          }),

          renderByModule(SystemModulesEnum.ADMIN_AREAS, {
            title: "Áreas",
            to: ROUTER_PATHS.administration.areaNav,
            level: 2,
          }),

          renderByModule(SystemModulesEnum.ADMIN_TIPO_REGIMEN, {
            title: "Tipo de Régimen",
            to: ROUTER_PATHS.administration.tipoRegimenesNav,
            level: 2,
          }),

          renderByModule(SystemModulesEnum.ADMIN_CODIGOS_OTP, {
            title: "Códigos OTP",
            to: ROUTER_PATHS.administration.codigosOTPNav,
            level: 2,
          }),

          renderByModule(SystemModulesEnum.ADMIN_DEPARTAMENTOS, {
            title: "Departamento",
            to: ROUTER_PATHS.administration.departamento_nav,
            level: 2,
          }),

          renderByModule(SystemModulesEnum.ADMIN_CANALES_VENTA, {
            title: "Canales de Venta",
            to: ROUTER_PATHS.administration.canalVentaNav,
            level: 2,
          }),

          renderByModule(SystemModulesEnum.ADMIN_COMICIONES_CONFIGURACION, {
            title: "Configuración Comisiones",
            to: ROUTER_PATHS.administration.configuracion_comiciones_nav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.ADMIN_ENTIDADES_FINANCIERAS, {
            title: "Entidades financieras",
            to: ROUTER_PATHS.administration.entidadesFinancieras_nav,
            level: 2,
          }),

          renderByModule(SystemModulesEnum.ADMIN_COOPERATIVAS, {
            //*TODO: Cambiar en el backend
            title: "Cooperativas",
            to: ROUTER_PATHS.administration.cooperativa_nav,
            level: 2,
          }),

          renderByModule(SystemModulesEnum.ADMIN_TARJETAS, {
            title: "Tarjetas",
            to: ROUTER_PATHS.administration.tarjetas_nav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.ADMIN_CENTRO_COSTO, {
            title: "Centro costo",
            to: ROUTER_PATHS.administration.centroCostoNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.ADMIN_MOTIVO, {
            title: "Motivos",
            to: ROUTER_PATHS.administration.motivos_nav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.ADMIN_MOTIVO_RECHAZO, {
            title: "Motivo rechazo",
            to: ROUTER_PATHS.administration.motivos_rechazo_nav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.ADMIN_UNIDADES_TRANSPORTE, {
            title: "Unidad transporte",
            to: ROUTER_PATHS.administration.unidades_transporte_nav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.ADMIN_RECEPCION_EQUIPOS_CATALOGOS, {
            title: "Catálogos Retiro de Equipos",
            to: ROUTER_PATHS.administration.recepcionEquiposCatalogosNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.ADMIN_ICE, {
            title: "ICE",
            to: ROUTER_PATHS.administration.ICENav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.ADMIN_PARAMETRO, {
            title: "Parámetros del Sistema",
            to: ROUTER_PATHS.administration.systemParametersNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.ADMIN_PORCENTAJE_RET_IVA_BIENES, {
            title: "Porcentaje retención IVA Bienes",
            to: ROUTER_PATHS.administration.porcentajes_ret_iva_bienes_nav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.ADMIN_PORCENTAJE_RET_IVA_SERVICIOS, {
            title: "Porcentaje retención IVA Servicios",
            to: ROUTER_PATHS.administration.porcentajes_ret_iva_servicios_nav,
            level: 2,
          }),

          renderByModule(SystemModulesEnum.ADMIN_AUDITORIA, {
            title: "Auditoría del Sistema",
            to: ROUTER_PATHS.administration.auditoriaNav,
            level: 2,
          }),

          renderByModule(SystemModulesEnum.GESTION_ACCESOS, {
            title: "Gestión de Accesos",
            to: ROUTER_PATHS.administration.gestionAccesosNav,
            level: 2,
          }),

          // Rotacion: visible si el user tiene el modulo GESTION_ACCESOS
          // O alguno de los permisos finos rotacion.* (asi un supervisor
          // con permiso fino tambien lo ve sin necesidad del modulo).
          (hasSystemModule(SystemModulesEnum.GESTION_ACCESOS)
            || hasPermission('rotacion.gestionar' as PermissionsEnum)
            || hasPermission('rotacion.ver' as PermissionsEnum))
            ? {
              title: "Rotación de Roles",
              to: ROUTER_PATHS.administration.rotacionRolesNav,
              level: 2,
            }
            : null,

          /* renderByModule(SystemModulesEnum.ADMIN_PORCENTAJE_RET_IVA_SERVICIOS, {
            title: 'Cargar Documentación',
            to: ROUTER_PATHS.administration.cargar_documentacion_nav,
            level: 2,
          }), */
        ].filter((item) => !!item),
      },
      ///* CONTROL CAMBIOS ---------------------------------
      {
        title: "Control de cambios",
        icon: (
          <SvgIcon fontSize="small">
            <PublishedWithChangesIcon />
          </SvgIcon>
        ),
        level: NestedMenuItemLevels.firstLevelWithChildren,
        items: [

          renderByModule(SystemModulesEnum.CONTROL_CAMBIOS_AREAS, {
            title: "Area de control de cambios",
            to: ROUTER_PATHS.control_cambios.areaNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CONTROL_CAMBIOS_APROBADORES, {
            title: "Aprobadores control de cambios",
            to: ROUTER_PATHS.control_cambios.aprobadorareanav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CONTROL_CAMBIOS_CAMBIOS, {
            title: "Control de cambios",
            to: ROUTER_PATHS.control_cambios.cambioServicenav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CONTROL_CAMBIOS_MIS_APROBACIONES, {
            title: "Mis aprobaciones",
            to: ROUTER_PATHS.control_cambios.cambioServiceMisAprobacionesNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CONTROL_CAMBIOS_RESPONSABLES, {
            title: "Responsables del control de cambios",
            to: ROUTER_PATHS.control_cambios.responsableNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CONTROL_CAMBIOS_MAILS, {
            title: "Correos para enviar notificaciones",
            to: ROUTER_PATHS.control_cambios.notificacionNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CONTROL_CAMBIOS_SERVIDORES, {
            title: "Servidores",
            to: ROUTER_PATHS.control_cambios.servidorNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CONTROL_CAMBIOS_MIS_CAMBIOS, {
            title: "Control de cambios personal",
            to: ROUTER_PATHS.control_cambios.misCambiosNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CONTROL_CAMBIOS_TIPO_RIESGO, {
            title: "Tipos de riesgo",
            to: ROUTER_PATHS.control_cambios.tipoRiesgoNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CONTROL_CAMBIOS_TIPO_ACCION, {
            title: "Tipo Accion para el control de cambios",
            to: ROUTER_PATHS.control_cambios.tipoAccionNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CONTROL_CAMBIOS_TIPO_METRICA, {
            title: "Tipo de metricas para el control de cambios",
            to: ROUTER_PATHS.control_cambios.tipoMetricaNav,
            level: 2,
          }),

        ].filter((item) => !!item),

      },

      ///* Mantenimiento y Operaciones --------------------------------
      {
        title: "Manteniemiento y Operación",
        icon: (
          <SvgIcon fontSize="small">
            <WrenchIcon />
          </SvgIcon>
        ),
        level: NestedMenuItemLevels.firstLevelWithChildren,
        items: [
          renderByModule(SystemModulesEnum.MANTEMIENTO_FLOTAS, {
            title: "Flotas",
            to: ROUTER_PATHS.mantenimientoOperaciones.flotaNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.MANTENIMIENTO_ZONAS, {
            title: "Zonas",
            to: ROUTER_PATHS.mantenimientoOperaciones.zonasNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.MANTENIMIENTO_SECTORES, {
            title: "Sectores",
            to: ROUTER_PATHS.mantenimientoOperaciones.sectoresNav,
            level: 2,
          }),
          // renderByModule(SystemModulesEnum.MANTENIMIENTO_NODOS, {
          //   title: "Nodo Gpon",
          //   to: ROUTER_PATHS.mantenimientoOperaciones.nodosNav,
          //   level: 2,
          // }),
          renderByModule(SystemModulesEnum.MANTENIMIENTO_RADIO_BASE, {
            title: "Radio Bases",
            to: ROUTER_PATHS.mantenimientoOperaciones.radioBasesNav,
            level: 2,
          }),
          // renderByModule(SystemModulesEnum.MANTENIMIENTO_NAP, {
          //   title: "Nap",
          //   to: ROUTER_PATHS.mantenimientoOperaciones.napsNav,
          //   level: 2,
          // }),
          renderByModule(SystemModulesEnum.MANTENIMIENTO_PLANIFICADOR, {
            title: "Planificador",
            to: ROUTER_PATHS.mantenimientoOperaciones.planificadorNav,
            level: 2,
          }),

          renderByModule(SystemModulesEnum.MANTEMIENTO_PENDIENTE_ACTUALIZAR, {
            title: "Pendiente Actualizar Técnico",
            to: ROUTER_PATHS.mantenimientoOperaciones
              .pendienteActualizarOperacionesNav,
            level: 2,
          }),

          renderByModule(SystemModulesEnum.MANTEMIENTO_RECHAZADAS_TECNICO, {
            title: "Rechazadas Técnico",
            to: ROUTER_PATHS.mantenimientoOperaciones.rechazadasTecnicoNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.MANTEMIENTO_PENDIENTE_ACTUALIZAR, {
            ///* TODO:Cambiar permisos del modulo
            title: "Solicitud Técnico",
            to: ROUTER_PATHS.mantenimientoOperaciones.solicitudTecnicoPymesNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.MANTEMIENTO_INSTALACIONES_TECNICOS, {
            ///* TODO:Cambiar permisos del modulo
            title: "Instalaciones Técnicos",
            to: ROUTER_PATHS.mantenimientoOperaciones.instalacionesTecnicosNav,
            level: 2,
          }),
        ].filter((item) => !!item),
      },

      ///* Ventas --------------------------------
      {
        title: "Ventas",
        level: NestedMenuItemLevels.firstLevelWithChildren,
        icon: (
          <SvgIcon fontSize="small">
            <PresentationChartLineIcon />
          </SvgIcon>
        ),
        items: [
          renderByModule(SystemModulesEnum.VENTAS_PROSPECTOS, {
            title: "Prospecto",
            to: ROUTER_PATHS.ventas.prospectoCrear,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.VENTAS_PREVENTAS, {
            title: "Preventas",
            to: ROUTER_PATHS.ventas.preventasNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.VENTAS_PREAGENDAMIENTOS, {
            title: "Preagendadas",
            to: ROUTER_PATHS.ventas.preAgendamientoNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.VENTAS_AGENDADAS, {
            title: "Agendadas",
            to: ROUTER_PATHS.ventas.agendamientoAgendadoNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.VENTAS_RECHAZADO_VENTAS, {
            title: "Prerechazados por PyL",
            to: ROUTER_PATHS.ventas.preRechazadosVentasNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.VENTAS_PENDIENTE_ACTUALIZAR, {
            title: "Pendiente Actualización",
            to: ROUTER_PATHS.ventas.agendamientoPendienteActualizacionNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.VENTAS_AGENDADAS, {
            title: "Referidos",
            to: ROUTER_PATHS.ventas.referidosNav,
            level: 2,
          }),
          {
            title: "Instalación",
            level: NestedMenuItemLevels.firstLevelWithChildren,
            items: [
              renderByModule(SystemModulesEnum.VENTAS_AGENDADAS, {
                title: "Finalizadas",
                to: ROUTER_PATHS.ventas.agendamientoFinalizadoAgendadoNav,
                level: 3,
              }),

              /* renderByModule(SystemModulesEnum.VENTAS_INSTALACIONES_ASIGNADAS, {
                title: "Asignado",
                to: ROUTER_PATHS.ventas.instalacionAsignadoNav,
                level: 3,
              }),
              renderByModule(
                SystemModulesEnum.VENTAS_INSTALACIONES_REALIZADAS,
                {
                  title: "Realizado",
                  to: ROUTER_PATHS.ventas.instalacionRealizadoNav,
                  level: 3,
                }
              ), */
            ].filter((item) => !!item),
          },
          renderByModule(SystemModulesEnum.VENTAS_BITACORA, {
            title: "Bitácora",
            to: ROUTER_PATHS.ventas.bitacoraVentasNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.HISTORIAL_VENTAS, {
            title: "Auditoría",
            to: ROUTER_PATHS.ventas.auditoriaVentasNav,
            level: 2,
          }),
        ].filter((item) => !!item) as NestedMenuItem[],
      },

      ///* Supervision de ventas  --------------------------------
      {
        title: "Supervisión de Ventas",
        level: NestedMenuItemLevels.firstLevelWithChildren,
        icon: (
          <SvgIcon fontSize="small">
            <WalletIcon />
          </SvgIcon>
        ),
        items: [
          renderByModule(SystemModulesEnum.SUPERVISION_VENTAS_PROSPECTO, {
            title: "Prospecto",
            to: ROUTER_PATHS.ventas.prospectoNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.SUPERVISION_VENTAS_PREVENTA, {
            title: "Preventa",
            to: ROUTER_PATHS.ventas.preventasNav,
            level: 2,
          }),
          renderByModule(
            SystemModulesEnum.SUPERVISION_VENTAS_PREAGENDAMIENTOS,
            {
              title: "Preagendadas",
              to: ROUTER_PATHS.ventas.preAgendamientoNav,
              level: 2,
            },
          ),
          renderByModule(SystemModulesEnum.SUPERVISION_VENTAS_RECHAZADO, {
            title: "Prerechazados por PyL",
            to: ROUTER_PATHS.ventas.preRechazadosVentasNav,
            level: 2,
          }),
          renderByModule(
            SystemModulesEnum.SUPERVISION_VENTAS_SOLICITUD_LIBERACION_VALOR_INSTALACION,
            {
              title: "Reporte de Venta EVA",
              to: ROUTER_PATHS.supervisionVentas.reporteVentasEva,
              level: 2,
            },
          ),
          renderByModule(
            SystemModulesEnum.SUPERVISION_VENTAS_CLIENTES_FIBRA_OPTICA,
            {
              title: "Clientes", // Fibra Óptica',
              to: ROUTER_PATHS.supervisionVentas.clientesServicio,
              level: 2,
            },
          ),
          renderByModule(SystemModulesEnum.SUPERVISION_VENTAS_BITACORA, {
            title: "Bitácora",
            to: ROUTER_PATHS.ventas.bitacoraVentasNav,
            level: 2,
          }),
          renderByModule(
            SystemModulesEnum.SUPERVISION_VENTAS_INSTALACION_PROGRAMADAS,
            {
              title:
                "Informe de Instalaciones Programadas y Resultados Diarios",
              to: ROUTER_PATHS.ventas.agendamientoReporteNav,
              level: 2,
            },
          ),
          renderByModule(
            SystemModulesEnum.SUPERVISION_VENTAS_SEGUIMIENTO_ACTIVIDADES,
            {
              title: "Seguimiento de Actividades",
              to: ROUTER_PATHS.supervisionVentas.seguimientoActividadesNav,
              level: 2,
            },
          ),
          {
            title: "Solicitud",
            level: NestedMenuItemLevels.firstLevelWithChildren,
            items: [
              renderByModule(
                SystemModulesEnum.SUPERVISION_VENTAS_SOLICITUD_INGRESO_PROSPECTO,
                {
                  title: "Solicitud Ingreso Beneficio",
                  to: ROUTER_PATHS.ventas.solicitudIngresoProspectoNav,
                  level: 3,
                },
              ),
              renderByModule(
                SystemModulesEnum.SUPERVISION_VENTAS_SOLICITUD_LIBERACION,
                {
                  title: "Solicitud Línea de Venta",
                  to: ROUTER_PATHS.supervisionVentas.liberacionVentasLineaNav,
                  level: 3,
                },
              ),
              renderByModule(
                SystemModulesEnum.SUPERVISION_VENTAS_SOLICITUD_LIBERACION_PROSPECTO,
                {
                  title: "Solicitud Liberación de Prospecto",
                  to: ROUTER_PATHS.supervisionVentas
                    .liberacionVentaProspectoNav,
                  level: 3,
                },
              ),
              renderByModule(
                SystemModulesEnum.SUPERVISION_VENTAS_SOLICITUD_LIBERACION_VALOR_INSTALACION,
                {
                  title: "Solicitud de Liberación para Valor de Instalación",
                  to: ROUTER_PATHS.supervisionVentas
                    .solicitudLiberacionValorInstalacionNav,
                  level: 3,
                },
              ),
              renderByModule(
                SystemModulesEnum.SUPERVISION_VENTAS_SOLICITUD_LIBERACION_VALOR_INSTALACION,
                {
                  title: "Solicitud Contrato Físico",
                  to: ROUTER_PATHS.supervisionVentas.solicitudContratoFisicoNav,
                  level: 3,
                },
              ),

              renderByModule(
                SystemModulesEnum.SUPERVISION_VENTAS_SOLICITUD_PASAR_DEUDA,
                {
                  title: "Solicitud Pasar Deuda",
                  to: ROUTER_PATHS.supervisionVentas
                    .reporteSolicitudPasarDeudaNav,
                  level: 3,
                },
              ),

              renderByModule(
                SystemModulesEnum.SUPERVISION_VENTAS_SOLICITUD_VENTA_EXTRANJERO,
                {
                  title: "Solicitud Extranjero",
                  to: ROUTER_PATHS.supervisionVentas.solicitudExtranjeroNav,
                  level: 3,
                },
              ),

              renderByModule(
                SystemModulesEnum.SUPERVISION_VENTAS_SOLICITUD_GREMIO,
                {
                  title: "Solicitud Promoción Gremios",
                  to: ROUTER_PATHS.supervisionVentas.solicitudPromoGremiosNav,
                  level: 3,
                },
              ),
              renderByModule(
                SystemModulesEnum.SUPERVISION_VENTAS_CODIGO_OTP_SOLICITUD_APROBACION,
                {
                  title: "Solicitud de Aprobación de Código",
                  to: ROUTER_PATHS.supervisionVentas
                    .solicitudAprobacionCodigosOTPNav,
                  level: 3,
                },
              ),
            ].filter((item) => !!item),
          },

          /* {
            title: "Codigo OTP",
            level: NestedMenuItemLevels.firstLevelWithChildren,
            items: [
              renderByModule(
                SystemModulesEnum.SUPERVISION_VENTAS_CODIGO_OTP_SOLICITUD_APROBACION,
                {
                  title: "Solicitud de Aprobación de Código",
                  to: ROUTER_PATHS.supervisionVentas
                    .solicitudAprobacionCodigosOTPNav,
                  level: 3,
                }
              ),
            ].filter((item) => !!item),
          }, */
        ].filter((item) => !!item),
      },

      {
        title: "Activaciones",
        level: NestedMenuItemLevels.firstLevelWithChildren,
        icon: (
          <SvgIcon fontSize="small">
            <CubeIcon />
          </SvgIcon>
        ),
        items: [
          {
            title: "Instalaciones",
            level: NestedMenuItemLevels.firstLevelWithChildren,
            items: [
              renderByModule(
                SystemModulesEnum.OPERACIONES_ACTIVACION_INSTALACIONES_INSTALACIONES,
                {
                  title: "Instalaciones",
                  to: ROUTER_PATHS.activaciones.activacionesInstalacionNav,
                  level: 3,
                },
              ),
              renderByModule(
                SystemModulesEnum.OPERACIONES_ACTIVACION_INSTALACIONES_FOTOS,
                {
                  title: "Fotos",
                  to: ROUTER_PATHS.activaciones.activacionInstallFotosNav,
                  level: 3,
                },
              ),
              // // ya no se hace solicitud de cambio de puerto/nap
              // renderByModule(
              //   SystemModulesEnum.OPERACIONES_ACTIVACION_INSTALACIONES_CAMBIO_PUERTO,
              //   {
              //     title: 'Cambio de NAP/Puerto',
              //     to: ROUTER_PATHS.activaciones
              //       .activacionInstallCambioPuertoNav,
              //     level: 3,
              //   }
              // ),
            ].filter((item) => !!item),
          },
          {
            title: "Activicaciones Corporativos",
            level: NestedMenuItemLevels.firstLevelWithChildren,
            items: [
              renderByModule(
                SystemModulesEnum.OPERACIONES_ACTIVACION_INSTALACIONES_INSTALACIONES,
                {
                  title: "Instalaciones",
                  to: ROUTER_PATHS.activaciones.tecnicosInstalacionCorpNav,
                  level: 3,
                },
              ),
            ].filter((item) => !!item),
          },

          renderByModule(SystemModulesEnum.ACTIVACIONES_RECHAZADAS_TECNICO, {
            title: "Rechazadas Técnico",
            to: ROUTER_PATHS.activaciones.activacionRechazadasTecnicoNav,
            level: 2,
          }),
          // ACTIVACIONES_CAJAS_PUERTOS_DISPONIBLES
          renderByModule(
            SystemModulesEnum.ACTIVACIONES_CAJAS_PUERTOS_DISPONIBLES,
            {
              title: "Cajas y Puertos Disponibles",
              to: ROUTER_PATHS.activaciones
                .activacionCajasPuertosDisponiblesNav,
              level: 2,
            },
          ),
          renderByModule(
            SystemModulesEnum.ACTIVACIONES_ACTUALIZACION_SERVICIO_CLIENTES,
            {
              title: "Actualización de Servicio a Clientes",
              to: ROUTER_PATHS.activaciones.activacionActServicioClientesNav,
              level: 2,
            },
          ),
          renderByModule(SystemModulesEnum.ACTIVACIONES_RAD_CHECK, {
            title: "RAD Check",
            to: ROUTER_PATHS.activaciones.activacionRadCheckNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.ACTIVACIONES_VISITA, {
            title: "Visita",
            to: ROUTER_PATHS.activaciones.activacionesVisitaNav,
            level:2
          }),
          renderByModule(SystemModulesEnum.ACTIVACIONES_TRASLADO,{
            title:"Traslados",
            to: ROUTER_PATHS.activaciones.activacionesTrasladoNav,
            level:2
          }),

          {
            title: "Consultas",
            level: NestedMenuItemLevels.firstLevelWithChildren,
            items: [
              renderByModule(
                SystemModulesEnum.ACTIVACIONES_CONSULTAS,
                {
                  title: "Por Tipo",
                  to: ROUTER_PATHS.activaciones.activacionesConsultasNav,
                  level: 3,
                },
              ),
            ].filter((item) => !!item),
          },

        ].filter((item) => !!item),
      },
      ///* Supervisor PYMESlevel: 2,
      {
        title: "Supervisor Pymes",
        level: NestedMenuItemLevels.firstLevelWithChildren,
        icon: (
          <SvgIcon fontSize="small">
            <SupervisedUserCircle />
          </SvgIcon>
        ),
        items: [
          renderByModule(
            SystemModulesEnum.SUPERVISION_PYMES_SOLICITUD_CONTRATO_FISICO,
            {
              title: "Contrato fisico",
              to: ROUTER_PATHS.clientes.clientesPymesNav,
              level: 2,
            },
          ),
        ].filter((item) => !!item),
      },

      ///* Ventas PYMES
      {
        title: "Ventas Pymes",
        level: NestedMenuItemLevels.firstLevelWithChildren,
        icon: (
          <SvgIcon fontSize="small">
            <Radar />
          </SvgIcon>
        ),
        items: [
          renderByModule(SystemModulesEnum.VENTAS_PYMES_PROSPECTOS, {
            title: "Prospecto",
            to: ROUTER_PATHS.ventasPymes.prospectoCreateNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.VENTAS_PYMES_PREVENTAS, {
            title: "Preventa",
            to: ROUTER_PATHS.ventasPymes.preventaNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.VENTAS_PYMES_PREAGENDAMIENTOS, {
            title: "Preagendadas",
            to: ROUTER_PATHS.ventasPymes.preagendamientoNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.VENTAS_PYMES_AGENDAMIENTO, {
            title: "Agendadas",
            to: ROUTER_PATHS.ventasPymes.agendamientoNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.VENTAS_PYMES_GESTION_VENTA, {
            title: "Gestion Venta",
            to: ROUTER_PATHS.ventasPymes.gestorVenta,
            level: 2,
          }),
        ].filter((item) => !!item),
      },

      {
        title: "Soporte Pymes",
        icon: (
          <SvgIcon fontSize="small">
            <Highlight />
          </SvgIcon>
        ),
        level: NestedMenuItemLevels.firstLevelWithChildren,
        items: [
          renderByModule(SystemModulesEnum.SOPORTE_PYMES_AGENDAMIENTO, {
            ///* TODO: Cambiar permiso del modulo
            title: "Agendamiento",
            to: ROUTER_PATHS.soportePymes.preagendamientoNav,
            level: 2,
          }),
        ].filter((item) => !!item),
      },

      /////* Operaciones --------------------------------
      {
        title: "Operaciones",
        level: NestedMenuItemLevels.firstLevelWithChildren,
        icon: (
          <SvgIcon fontSize="small">
            <CubeIcon />
          </SvgIcon>
        ),
        items: [
          {
            title: "Planeación",
            level: NestedMenuItemLevels.firstLevelWithChildren,
            items: [
              renderByModule(
                SystemModulesEnum.OPERACIONES_PLANEACION_AGENDAMIENTOS,
                {
                  title: "Agendamientos",
                  to: ROUTER_PATHS.operaciones.agendamientoPyLNav,
                  level: 3,
                },
              ),
              renderByModule(
                SystemModulesEnum.OPERACIONES_PLANEACION_AGENDAMIENTOS_FINALIZADOS,
                {
                  title: "Finalizados",
                  to: ROUTER_PATHS.operaciones.agendamientoPyLFinalizados,
                  level: 3,
                },
              ),
              // OPERACIONES_PLANEACION_AGENDAMIENTOS_SEGUIMIENTO
              renderByModule(
                SystemModulesEnum.OPERACIONES_SEGUIMIENTO_ACTIVIDADES,
                {
                  title: "Seguimiento de Actividades",
                  to: ROUTER_PATHS.operaciones.agendamientoPyLSeguimientoNav,
                  level: 3,
                },
              ),
              renderByModule(SystemModulesEnum.OPERACIONES_AGENDA, {
                title: "Agenda de Flotas",
                to: ROUTER_PATHS.operaciones.flotasViewNav,
                level: 3,
              }),
            ].filter((item) => !!item),
          },
          renderByModule(SystemModulesEnum.OPERACIONES_BITACORA, {
            title: "Bitácora",
            to: ROUTER_PATHS.operaciones.bitacoraPylNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.OPERACIONES_RECHAZADAS_TECNICO, {
            title: "Rechazadas Técnico",
            to: ROUTER_PATHS.operaciones.rechazadasTecnicoNav,
            level: 2,
          }),
          renderByModule(
            SystemModulesEnum.OPERACIONES_INFORME_INSTALACION_PROGRAMADAS,
            {
              title:
                "Informe de Instalaciones Programadas y Resultados Diarios",
              to: ROUTER_PATHS.operaciones.InformeInstalacionPylNav,
              level: 2,
            },
          ),

          renderByModule(SystemModulesEnum.OPERACIONES_BITACORA, {
            title: "Traslados",
            to: ROUTER_PATHS.operaciones.trasladosPyLNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.OPERACIONES_BITACORA, {
            title: "Visitas",
            to: ROUTER_PATHS.operaciones.visitasPylNav,
            level: 2,
          }),

          // {
          //   title: "Activaciones",
          //   level: NestedMenuItemLevels.firstLevelWithChildren,
          //   items: [
          //     {
          //       title: "Activaciones Home",
          //       level: 3, //NestedMenuItemLevels.firstLevelWithChildren,
          //       items: [
          //         renderByModule(
          //           SystemModulesEnum.OPERACIONES_ACTIVACION_INSTALACIONES_INSTALACIONES,
          //           {
          //             title: "Instalaciones",
          //             to: ROUTER_PATHS.operaciones.activacionesInstalacionNav,
          //             level: 3,
          //           }
          //         ),
          //         renderByModule(
          //           SystemModulesEnum.OPERACIONES_ACTIVACION_INSTALACIONES_FOTOS,
          //           {
          //             title: "Fotos",
          //             to: ROUTER_PATHS.operaciones.activacionInstallFotosNav,
          //             level: 3,
          //           }
          //         ),
          //         // // ya no se hace solicitud de cambio de puerto/nap
          //         // renderByModule(
          //         //   SystemModulesEnum.OPERACIONES_ACTIVACION_INSTALACIONES_CAMBIO_PUERTO,
          //         //   {
          //         //     title: 'Cambio de NAP/Puerto',
          //         //     to: ROUTER_PATHS.operaciones
          //         //       .activacionInstallCambioPuertoNav,
          //         //     level: 3,
          //         //   }
          //         // ),
          //       ].filter((item) => !!item),
          //     },
          //     {
          //       title: "Activaciones Corporativos",
          //       level: 3,//NestedMenuItemLevels.firstLevelWithChildren,
          //       items: [
          //         renderByModule(
          //           SystemModulesEnum.OPERACIONES_ACTIVACION_INSTALACIONES_INSTALACIONES,
          //           {
          //             title: "Instalaciones",
          //             to: ROUTER_PATHS.operaciones.tecnicosInstalacionCorpNav,
          //             level: 3,
          //           }
          //         ),
          //       ].filter((item) => !!item),
          //     }
          //   ].filter((item) => !!item),
          // },

          {
            title: "Técnico",
            level: NestedMenuItemLevels.firstLevelWithChildren,
            items: [
              renderByModule(
                SystemModulesEnum.OPERACIONES_TECNICO_INSTALACIONES,
                {
                  title: "Instalaciones",
                  to: ROUTER_PATHS.operaciones.tecnicosInstalacionNav,
                  level: 3,
                },
              ),
            ].filter((item) => !!item),
          },

          {
            title: "Auditoría",
            level: NestedMenuItemLevels.firstLevelWithChildren,
            items: [
              renderByModule(
                SystemModulesEnum.OPERACIONES_AUDITORIA_INSTALACIONES,
                {
                  title: "Instalaciones",
                  to: ROUTER_PATHS.operaciones.auditoriaInstalacionesNav,
                  level: 3,
                },
              ),
            ].filter((item) => !!item),
          },

          /* renderByModule(SystemModulesEnum.OPERACIONES_AUDITORIA_INSTALACIONES, {
            title: 'Mensajeria',
            level: 2,
            to: ROUTER_PATHS.operaciones.mensajeriaNav,
          }), */
        ].filter((item) => !!item),
      },

      ///* Supervisión Ventas cooperativas
      {
        title: "Supervisión Venta Corporativo",
        level: NestedMenuItemLevels.firstLevelWithChildren,
        icon: (
          <SvgIcon fontSize="small">
            <Business />
          </SvgIcon>
        ),
        items: [
          renderByModule(SystemModulesEnum.PLANES_CORPORATIVOS, {
            title: "Planes Corporativos",
            to: ROUTER_PATHS.ventasCorporativas.planCorporativoNav,
            level: 2,
          }),
          renderByModule(
            SystemModulesEnum.SOLICITUDES_PENDIENTES_CORPORATIVOS,
            {
              title: "Solicitudes Pendientes",
              to: ROUTER_PATHS.ventasCorporativas
                .solicitudesPendientesCorporativoNav,
              level: 2,
            },
          ),
          renderByModule(SystemModulesEnum.SOLICITUDES_CAPACIDAD_CORPORATIVOS, {
            title: "Solicitudes Enlace",
            to: ROUTER_PATHS.ventasCorporativas.solicitudCapacidadNav,
            level: 2,
          }),

          renderByModule(SystemModulesEnum.SOLICITUDES_CAPACIDAD_CORPORATIVOS, {
            title: "Solicitudes Nueva Linea",
            to: ROUTER_PATHS.ventasCorporativas.solicitudNuevaLineaNav,
            level: 2,
          }),
        ].filter((item) => !!item) as NestedMenuItem[],
      },

      ///* Ventas cooperativas
      {
        title: "Venta Corporativo",
        level: NestedMenuItemLevels.firstLevelWithChildren,
        icon: (
          <SvgIcon fontSize="small">
            <Business />
          </SvgIcon>
        ),
        items: [
          renderByModule(SystemModulesEnum.VENTAS_PROSPECTOS_CORPORATIVOS, {
            title: "Prospecto",
            to: ROUTER_PATHS.ventasCorporativas.prospectoCrearCorporativo,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.VENTAS_PREVENTAS_CORPORATIVOS, {
            title: "Oportunidad Comercial",
            to: ROUTER_PATHS.ventasCorporativas.preventaCorporativoNav,
            level: 2,
          }),
          renderByModule(
            SystemModulesEnum.VENTAS_SOLUCION_COSTEADA_CORPORATIVOS,
            {
              title: "Solución Costeada",
              to: ROUTER_PATHS.ventasCorporativas
                .solucionCosteadaCorporativoNav,
              level: 2,
            },
          ),
          renderByModule(SystemModulesEnum.VENTAS_ESTADO_VENTA_CORPORATIVOS, {
            title: "Estado de Venta",
            to: ROUTER_PATHS.ventasCorporativas.estadoVentaCorporativoNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.VENTAS_OPORTUNIDAD_COMERCIAL_DEMO, {
            title: "Oportunidad Comercial DEMO",
            to: ROUTER_PATHS.ventasCorporativas.oportunidadComercialDemoNav,
            level: 2,
          }),
          // SystemModulesEnum.VENTAS_GESTION_OPORTUNIDAD_COMERCIAL
          renderByModule(
            SystemModulesEnum.VENTAS_GESTION_OPORTUNIDAD_COMERCIAL,
            {
              title: "Gestion Oportunidad Comercial",
              to: ROUTER_PATHS.ventasCorporativas
                .gestionOportunidadComercialNav,
              level: 2,
            },
          ),
          renderByModule(SystemModulesEnum.VENTAS_COBERTURA_CORPORATIVA, {
            title: "Cobertura Corporativa",
            to: ROUTER_PATHS.ventasCorporativas.coberturaCorporativaNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.VENTAS_COBERTURA_NAPS, {
            title: "Cobertura Naps",
            to: ROUTER_PATHS.ventasCorporativas.coberturaNapsNav,
            level: 2,
          }),
        ].filter((item) => !!item) as NestedMenuItem[],
      },

      ///* NOC
      {
        title: "Preventa Corporativo",
        level: NestedMenuItemLevels.firstLevelWithChildren,
        icon: (
          <SvgIcon fontSize="small">
            <Dataset />
          </SvgIcon>
        ),
        items: [
          renderByModule(SystemModulesEnum.NOC_FACTIBILIDAD_CORPORATIVOS, {
            title: "Factibilidad Corporativo",
            to: ROUTER_PATHS.noc.factibilidadCorporativoNav,
            level: 2,
          }),
          //NOC_INSPECCION_FACTIBILIDAD_CORPORATIVOS
          renderByModule(
            SystemModulesEnum.NOC_INSPECCION_FACTIBILIDAD_CORPORATIVOS,
            {
              title: "Inspeccion Factibilidad",
              to: ROUTER_PATHS.noc.inspeccionCorporativoNav,
              level: 2,
            },
          ),
          renderByModule(SystemModulesEnum.NOC_AGENDAMIENTO, {
            title: "Agendamientos",
            to: ROUTER_PATHS.noc.agendamientoNocNav,
            level: 2,
          }),

          renderByModule(SystemModulesEnum.NOC_SOLICITUD_TECNICO, {
            title: "Solicitud Técnico",
            to: ROUTER_PATHS.noc.solicitudTecnicoNocNav,
            level: 2,
          }),
          renderByModule(
            SystemModulesEnum.VENTAS_SEGUIMIENTO_COSTE_CORPORATIVOS,
            {
              title: "Seguimiento y Coste",
              to: ROUTER_PATHS.ventasCorporativas.seguimientoCosteNav,
              level: 2,
            },
          ),
          renderByModule(
            SystemModulesEnum.NOC_SOLICITUD_CORPORATIVO,
            {
              title: "Solicitudes Externas",
              to: ROUTER_PATHS.solicitud_preventa.solicitudVerificarsolicitud,
              level: 2,
            },
          ),
          renderByModule(
            SystemModulesEnum.NOC_SOLICITUD_SUCURSAL,
            {
              title: "Solicitudes",
              to: ROUTER_PATHS.solicitud_preventa.solicitudNav,
              level: 2,
            },
          ),
        ].filter((item) => !!item) as NestedMenuItem[],
      },

      {
        title: "NOC",
        level: NestedMenuItemLevels.firstLevelWithChildren,
        icon: (
          <SvgIcon fontSize="small">
            <SettingsInputAntenna />
          </SvgIcon>
        ),
        items: [
          renderByModule(SystemModulesEnum.NOC_ORDENES_DE_TRABAJO, {
            title: "Ordenes De Trabajo",
            to: ROUTER_PATHS.noc.ordenesDeTrabajoNocListaNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.NOC_PARAMETROS_ORDENES_DE_TRABAJO, {
            title: "Parametros",
            to: ROUTER_PATHS.noc.ordenesDeTrabajoNocParametrosNav,
            level: 2,
          }),
        ].filter((item) => !!item) as NestedMenuItem[],
      },

      ///* Soporte Corporativo
      {
        title: "Soporte Corporativo",
        level: NestedMenuItemLevels.firstLevelWithChildren,
        icon: (
          <SvgIcon fontSize="small">
            <Dataset />
          </SvgIcon>
        ),
        items: [
          renderByModule(
            SystemModulesEnum.SOPORTE_CORPORATIVO_GESTION_TICKETS,
            {
              title: "Gestión de Tickets",
              to: ROUTER_PATHS.soporteCorporativo.gestionTicketsNav,
              level: 2,
            },
          ),
        ].filter((item) => !!item) as NestedMenuItem[],
      },

      ///* servicios
      ///* servicios
      {
        title: "Servicios",
        icon: (
          <SvgIcon fontSize="small">
            <QueueListIcon />
          </SvgIcon>
        ),
        level: NestedMenuItemLevels.firstLevelWithChildren,
        items: [
          renderByModule(SystemModulesEnum.SERVICIOS_INTERNET, {
            title: "Planes",
            to: ROUTER_PATHS.servicioInternet.planesNav,
            level: 2,
          }),
        ].filter((item) => !!item),
      },

      /////* Técnico --------------------------------
      {
        title: "Técnico",
        level: NestedMenuItemLevels.firstLevelWithChildren,
        icon: (
          <SvgIcon fontSize="small">
            <QueueListIcon />
          </SvgIcon>
        ),
        items: [
          {
            title: "Instalaciones",
            level: NestedMenuItemLevels.firstLevelWithChildren,
            items: [
              renderByModule(
                SystemModulesEnum.TECNICO_INSTALACIONES_INSTALACIONES,
                {
                  title: "Instalaciones",
                  to: ROUTER_PATHS.tecnico.ordenenTrabajoNav,
                  level: 3,
                },
              ),
              renderByModule(SystemModulesEnum.TECNICO_INSTALACIONES_FOTOS, {
                title: "Fotos",
                to: ROUTER_PATHS.tecnico.ordenTrabajoFotosNav,
                level: 3,
              }),
            ],
          },
          renderByModule(SystemModulesEnum.TECNICO_VISITAS, {
            title: "Visitas",
            to: ROUTER_PATHS.tecnico.visitasNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.TECNICO_RETIROS, {
            title: "Retiros",
            to: ROUTER_PATHS.tecnico.retirosNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.TECNICO_TRASLADOS, {
            title: "Traslados",
            to: ROUTER_PATHS.tecnico.trasladosNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.TECNICO_MIGRACIONES, {
            title: "Migraciones",
            to: ROUTER_PATHS.tecnico.migracionesNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.TECNICO_DETALLE_DE_MATERIALES, {
            title: "Detalle de Materiales",
            to: ROUTER_PATHS.tecnico.detalleMaterialesNav,
            level: 2,
          }),
          // TODO: check correct permissions
          renderByModule(SystemModulesEnum.TECNICO_NO, {
            title: "Transferencia Material",
            to: ROUTER_PATHS.tecnico.transferenciaMaterialNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.TECNICO_ATS, {
            title: "ATS",
            to: ROUTER_PATHS.tecnico.atsNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.TECNICO_DETALLE_DE_MATERIALES, {
            title: "Cuadre Técnico",
            to: ROUTER_PATHS.tecnico.cuadreNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.TECNICO_ORDENES_TRABAJO_NOC, {
            title: "Ordenes de Trabajo Noc",
            to: ROUTER_PATHS.tecnico.ordenesNocNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.TECNICO_NOTIFICACION_INVENTARIO, {
            title: "Notificación Inventario",
            to: ROUTER_PATHS.tecnico.notificacionInventarioNav,
            level: 2,
          }),
        ].filter((item) => !!item) as NestedMenuItem[],
      },
      {
        title: "Consultas Naps",
        level: NestedMenuItemLevels.firstLevelWithChildren,
        icon: (
          <SvgIcon fontSize="small">
            <NumberedListIcon />
          </SvgIcon>
        ),
        items: [
          renderByModule(SystemModulesEnum.TECNICO_CONSULTAS_NAPS, {
            title: "Por Tipo",
            to: ROUTER_PATHS.tecnico.consultasNapsNav,
            level: 2,
          }),
        ].filter((item) => !!item) as NestedMenuItem[],
      },

      /////* Técnicos corporativos -----------------------------------

      {
        title: "Técnicos Corporativos",
        level: NestedMenuItemLevels.firstLevelWithChildren,
        icon: (
          <SvgIcon fontSize="small">
            <WifiTethering />
          </SvgIcon>
        ),
        items: [
          {
            title: "Orden Servicio Corporativo",
            level: NestedMenuItemLevels.firstLevelWithChildren,
            items: [
              renderByModule(
                SystemModulesEnum.TECNICO_INSTALACIONES_INSTALACIONES,
                {
                  title: "Instalaciones",
                  to: ROUTER_PATHS.tecnico.servicioCorporativoNav,
                  level: 3,
                },
              ),
              renderByModule(SystemModulesEnum.TECNICO_INSTALACIONES_FOTOS, {
                title: "Fotos",
                to: ROUTER_PATHS.tecnico.ordenTrabajoFotosNav,
                level: 3,
              }),
            ].filter((item) => !!item) as NestedMenuItem[],
          },

          renderByModule(
            SystemModulesEnum.OPERACIONES_ACTIVACION_INSTALACIONES_INSTALACIONES,
            {
              title: "Activación",
              to: ROUTER_PATHS.operaciones.tecnicosInstalacionCorpNav,
              level: 2,
            },
          ),
        ].filter((item) => !!item) as NestedMenuItem[],
      },
      /////* Clientes --------------------------------
      {
        title: "Clientes",
        level: NestedMenuItemLevels.firstLevelWithChildren,
        icon: (
          <SvgIcon fontSize="small">
            <UserGroupIcon />
          </SvgIcon>
        ),
        items: [
          // renderByModule(SystemModulesEnum.CLIENTES_CLIENTES_CONTRATO, { ///* TODO: Cambiar por el backend
          //   title: 'Clientes Contratos',
          //   to: ROUTER_PATHS.clientes.clienteContratoNav,
          //   level: 2,
          // }),

          renderByModule(SystemModulesEnum.CLIENTES_CLIENTES_FIBRA, {
            title: "Clientes Fibra Óptica",
            to: ROUTER_PATHS.clientes.fibraOpticaNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CLIENTES_CLIENTES_WIRELESS, {
            title: "Clientes Wireless",
            to: ROUTER_PATHS.clientes.wirelessNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CLIENTES_CLIENTES_COORPORATIVOS, {
            title: "Clientes Corporativos",
            to: ROUTER_PATHS.clientes.corporativoNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CLIENTES_CLIENTES_COORPORATIVOS, {
            ///* TODO: Cambiar permiso del modulo
            title: "Clientes Pymes",
            to: ROUTER_PATHS.clientes.clientesPymesNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CLIENTES_CLIENTES_BRAS, {
            title: "Clientes BRAS",
            to: ROUTER_PATHS.clientes.clientesBrasNav,
            level: 2,
          }),
        ].filter((item) => !!item),
      },

      ///* Configuración RED  --------------------------------
      {
        title: "Configuración de Red",
        icon: (
          <SvgIcon fontSize="small">
            <GlobeAltIcon />
          </SvgIcon>
        ),
        level: NestedMenuItemLevels.firstLevelWithChildren,
        items: [
          renderByModule(SystemModulesEnum.CONFIGURACION_RED_GRUPO_IPV4, {
            title: "Grupos IPv4",
            to: ROUTER_PATHS.configuracionRED.grupoIpv4Nav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CONFIGURACION_RED_GRUPO_IPV6, {
            title: "Grupos IPv6",
            to: ROUTER_PATHS.configuracionRED.grupoIpv6Nav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CONFIGURACION_RED_IPV4, {
            title: "IPv4",
            to: ROUTER_PATHS.configuracionRED.ipv4Nav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CONFIGURACION_RED_OLT, {
            title: "OLT",
            to: ROUTER_PATHS.configuracionRED.oltNav,
            level: 2,
          }),
          // SystemModulesEnum.CONFIGURACION_RED_NODOS
          renderByModule(SystemModulesEnum.CONFIGURACION_RED_NODOS, {
            title: "Nodos",
            to: ROUTER_PATHS.configuracionRED.nodoNav,
            level: 2,
          }),
          // SystemModulesEnum.CONFIGURACION_RED_CAJA_PRIMARIA
          renderByModule(SystemModulesEnum.CONFIGURACION_RED_CAJA_PRIMARIA, {
            title: "Manga",
            to: ROUTER_PATHS.configuracionRED.cajaPrimariaNav,
            level: 2,
          }),
          // SystemModulesEnum.CONFIGURACION_RED_CAJA_SECUNDARIA
          renderByModule(SystemModulesEnum.CONFIGURACION_RED_CAJA_SECUNDARIA, {
            title: "NAP",
            to: ROUTER_PATHS.configuracionRED.napsNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CONFIGURACION_RED_ROUTERS, {
            title: "PPPoE (Router)",
            to: ROUTER_PATHS.configuracionRED.routersNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CONFIGURACION_RED_BRAS, {
            title: "BRAS",
            to: ROUTER_PATHS.configuracionRED.brasNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CONFIGURACION_RED_RADIUS, {
            title: "Radius",
            to: ROUTER_PATHS.configuracionRED.radiusNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CONFIGURACION_RED_OPERADORA, {
            title: "Operadora",
            to: ROUTER_PATHS.configuracionRED.operadoraNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CONFIGURACION_RED_RADIUS_MIGRACIONES, {
            title: "Migraciones BRAS",
            to: ROUTER_PATHS.configuracionRED.radiusMigracionesNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CONFIGURACION_RED_SERVICE_PROFILE, {
            title: "Administrar Service Profile",
            to: ROUTER_PATHS.configuracionRED.serviceProfileNav,
            level: 2,
          }),
        ].filter((item) => !!item),
      },

      ///* Usuarios  --------------------------------
      {
        title: "Usuarios",
        icon: (
          <SvgIcon fontSize="small">
            <UserPlusIcon />
          </SvgIcon>
        ),
        level: NestedMenuItemLevels.firstLevelWithChildren,
        items: [
          ///* USERS ---------------------
          renderByModule(SystemModulesEnum.USUARIOS_USUARIOS, {
            title: "Usuarios",
            to: ROUTER_PATHS.administration.usuariosNav,
            level: 2,
          }),

          renderByModule(SystemModulesEnum.USUARIOS_GRUPOS, {
            title: "Grupos de Sistema",
            to: ROUTER_PATHS.administration.grupoSistemaNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.USUARIOS_MFA_ADMIN, {
            title: "Administración MFA",
            to: ROUTER_PATHS.administration.mfaAdminNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.USUARIOS_REPORTE_USUARIOS, {
            title: "Reporte de Usuarios",
            to: ROUTER_PATHS.administration.reporteUsuariosNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.SOPORTE_TECNICO_TIKETS_USUARIOS, {
            title: "Crear tickets de usuarios",
            to: ROUTER_PATHS.tiketUser.ver_tikets_usuarios_nav,
            level: 2,
          }),
        ].filter((item) => !!item),
      },

      {
        title: "Catálogo Productos",
        icon: (
          <SvgIcon fontSize="small">
            <ClipboardDocumentCheckIcon />
          </SvgIcon>
        ),
        level: NestedMenuItemLevels.firstLevelWithChildren,
        items: [
          // TODO: set correct modules
          renderByModule(SystemModulesEnum.CATALOGO_PRODUCTO_INVENTARIABLES, {
            title: "Productos",
            to: ROUTER_PATHS.inventario.productosNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CATALOGO_PRODUCTO_COSTOS, {
            title: "Costos",
            to: ROUTER_PATHS.inventario.costos_nav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CATALOGO_PRODUCTO_GASTOS, {
            title: "Gastos",
            to: ROUTER_PATHS.inventario.gastos_nav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CATALOGO_PRODUCTO_SERVICIOS, {
            title: "Servicios",
            to: ROUTER_PATHS.inventario.servicio,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.ACTIVOS_ACTIVOS, {
            title: "Activos",
            to: ROUTER_PATHS.activos.activos_nav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CATALOGO_PRODUCTO_VENTAS, {
            title: "Ventas",
            to: ROUTER_PATHS.del.noRoute,
            level: 2,
          }),
        ].filter((item) => !!item),
      },
      {
        title: "Configuración Inventario",
        icon: (
          <SvgIcon fontSize="small">
            <AdjustmentsHorizontalIcon />
          </SvgIcon>
        ),
        level: NestedMenuItemLevels.firstLevelWithChildren,
        items: [
          // TODO: set correct modules
          renderByModule(
            SystemModulesEnum.CONFIGURACION_INVENTARIO_UNIDA_MEDIDA,
            {
              title: "Unidades de Medida",
              to: ROUTER_PATHS.inventario.medidas_productos_nav,
              level: 2,
            },
          ),
          renderByModule(
            SystemModulesEnum.CONFIGURACION_INVENTARIO_PRESENTACION,
            {
              title: "Presentaciones",
              to: ROUTER_PATHS.inventario.presentacion_nav,
              level: 2,
            },
          ),
          renderByModule(SystemModulesEnum.CONFIGURACION_INVENTARIO_CATEGORIA, {
            title: "Categorías",
            to: ROUTER_PATHS.inventario.categoria_productos_nav,
            level: 2,
          }),
          renderByModule(
            SystemModulesEnum.CONFIGURACION_INVENTARIO_SUBCATEGORIA,
            {
              title: "Subcategorías",
              to: ROUTER_PATHS.inventario.subcategoria_productos_nav,
              level: 2,
            },
          ),
          renderByModule(SystemModulesEnum.INVENTARIO_MODELO_PRODUCTO, {
            title: "Modelos de Productos",
            to: ROUTER_PATHS.inventario.modelo_productos_nav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.INVENTARIO_CATEGORIA_PRODUCTO, {
            title: "Categoria de Productos",
            to: ROUTER_PATHS.inventario.categoria_productos_nav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.INVENTARIO_SUBCATEGORIA_PRODUCTO, {
            title: "Subcategoria de Productos",
            to: ROUTER_PATHS.inventario.subcategoria_productos_nav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.INVENTARIO_MARCA_PRODUCTO, {
            title: "Marca de Productos",
            to: ROUTER_PATHS.inventario.marca_productos_nav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.INVENTARIO_UBICACIONES, {
            title: "Ubicaciones",
            to: ROUTER_PATHS.inventario.ubicacion_nav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.INVENTARIO_BODEGAS, {
            title: "Bodegas",
            to: ROUTER_PATHS.inventario.bodegas_nav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CONFIGURACION_INVENTARIO_GENERADOR_SERIES, {
            title: "Generador Series",
            to: ROUTER_PATHS.inventario.generador_series,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CONFIGURACION_INVENTARIO_CONFIGURACION_TRANSITO, {
            title: "Configuración de Tránsito",
            to: ROUTER_PATHS.inventario.configuracion_transito,
            level: 2,
          }),
        ].filter((item) => !!item),
      },
      {
        title: "Documentación",
        icon: (
          <SvgIcon fontSize="small">
            <ClipboardDocumentListIcon />
          </SvgIcon>
        ),
        items: [
          renderByModule(SystemModulesEnum.DOCUMENTACION_INVENTARIABLE, {
            title: "Documentos",
            to: ROUTER_PATHS.del.noRoute,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.DOCUMENTACION_KARDEX, {
            title: "Kardex",
            to: ROUTER_PATHS.inventario.kardexNav,
            level: 2,
          }),
        ].filter((item) => !!item),
      },

      {
        title: "Recaudaciones",
        icon: (
          <SvgIcon fontSize="small">
            <CurrencyDollarIcon />
          </SvgIcon>
        ),
        items: [
          renderByModule(SystemModulesEnum.RECAUDACIONES_RUBROS, {
            title: "Rubros",
            to: ROUTER_PATHS.del.noRoute,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.RECAUDACIONES_SALDOS, {
            title: "Saldos",
            to: ROUTER_PATHS.del.noRoute,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.RECAUDACIONES_TRANSACCIONES, {
            title: "Transacciones",
            to: ROUTER_PATHS.del.noRoute,
            level: 2,
          }),
        ].filter((item) => !!item),
      },

      {
        title: "Inventario",
        icon: (
          <SvgIcon fontSize="small">
            <ClipboardDocumentCheckIcon />
          </SvgIcon>
        ),
        level: NestedMenuItemLevels.firstLevelWithChildren,
        items: [
          renderByModule(SystemModulesEnum.INVENTARIO_INGRESO_INVENTARIO, {
            title: "Ingreso de Inventario",
            //to: ROUTER_PATHS.inventario.ingresoInventario,
            to: ROUTER_PATHS.inventario.ingresoHistorial,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.INVENTARIO_SOLICITUD_INVENTARIO, {
            title: "Solicitud para Transferencia de Inventario",
            to: ROUTER_PATHS.inventario.solicitudInventarioNav,
            level: 2,
          }),
          renderByModule(
            SystemModulesEnum.INVENTARIO_REVISAR_SOLICITUD_INVENTARIO,
            {
              title: "Revisión de Solicitud para Transferencia de Inventario",
              to: ROUTER_PATHS.inventario.revisarSolicitudInventarioNav,
              level: 2,
            },
          ),
          renderByModule(SystemModulesEnum.INVENTARIO_TRASLADO_INVENTARIO, {
            title: "Transferencia de Inventario",
            to: ROUTER_PATHS.inventario.trasladoInventario,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.INVENTARIO_RECEPCION_TRANSFERENCIA, {
            ///* TODO: Cambiar permisos
            title: "Recepción de Transferencia Inventario",
            to: ROUTER_PATHS.inventario.recepcionTransferenciaInventarioNav,
            level: 2,
          }),
          renderByModule(
            SystemModulesEnum.INVENTARIO_REVISAR_SOLICITUD_PEDIDO,
            {
              title: "Revisión de Solicitud de Pedido",
              to: ROUTER_PATHS.inventario.solicitud_pedido_nav,
              level: 2,
            },
          ),
          /* renderByModule(SystemModulesEnum.INVENTARIO_SOLICITUDES_DESPACHAR, {
            title: 'Solicitudes a despachar',
            to: ROUTER_PATHS.inventario.solicitud_all_action_nav,
            level: 2,
          }), */
          renderByModule(SystemModulesEnum.INVENTARIO_EGRESOS, {
            title: "Egresos de Inventario",
            to: ROUTER_PATHS.inventario.egresos_nav,
            level: 2,
          }),
          /* renderByModule(SystemModulesEnum.INVENTARIO_INGRESO, {
            title: "Recepción de ingreso",
            to: ROUTER_PATHS.inventario.solicitud_ingreso_inventario_nav,
            level: 2,
          }), */
          renderByModule(
            SystemModulesEnum.INVENTARIO_SOLICITUD_INGRESO_COMPRA,
            {
              title: "Solicitud de Compra",
              to: ROUTER_PATHS.inventario.solicitud_ingreso_compraNav,
              level: 2,
            },
          ),
          renderByModule(SystemModulesEnum.INVENTARIO_RECEPCION_MERCADERIA, {
            title: "Recepción de Mercadería",
            to: ROUTER_PATHS.inventario.recepcionCompraNav,
            level: 2,
          }),
          /*   renderByModule(SystemModulesEnum.INVENTARIO_ORDEN_COMPRA, {
              //TODO:CAMBIAR POR EL BACKEND
              title: "Recepción de Mercadería",
              to: ROUTER_PATHS.inventario.recepcionCompraNav,
              level: 2,
            }), */
          renderByModule(SystemModulesEnum.INVENTARIO_KIT, {
            title: "Kit Inventario",
            to: ROUTER_PATHS.inventario.kitInventariable,
            level: 2,
          }),
          renderByModule(
            SystemModulesEnum.INVENTARIO_SOLICITUD_FACTIBILIDAD_CORPORATIVA,
            {
              title: "Solicitud Factibilidad Corporativo",
              to: ROUTER_PATHS.inventario.solicitudFactibilidadCorporativoNav,
              level: 2,
            },
          ),
          renderByModule(SystemModulesEnum.INVENTARIO_REPORTE_STOCK, {
            title: "Reporte de Stock",
            to: ROUTER_PATHS.inventario.reporteStockNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.INVENTARIO_CUADRE_INVENTARIO, {
            title: "Cuadre de Inventario",
            to: ROUTER_PATHS.inventario.cuadreInventarioNav,
            level: 2,
          }),
        ].filter((item) => !!item) as NestedMenuItem[],
      },
      {
        title: "Pedidos",
        icon: (
          <SvgIcon fontSize="small">
            <LocalShipping />
          </SvgIcon>
        ),
        level: NestedMenuItemLevels.firstLevelWithChildren,
        items: [
          renderByModule(SystemModulesEnum.PEDIDOS_SOLICITUD_PEDIDO, {
            title: "Solicitud de Pedidos",
            to: ROUTER_PATHS.pedidos.solicitud_pedidos_nav,
            //to: ROUTER_PATHS.pedidos.solicitud_pedidos_crear,
            level: 2,
          }),
        ].filter((item) => !!item),
      },
      {
        title: "Compras",
        icon: (
          <SvgIcon fontSize="small">
            <ShoppingCartIcon />
          </SvgIcon>
        ),
        level: NestedMenuItemLevels.firstLevelWithChildren,
        items: [
          renderByModule(SystemModulesEnum.COMPRAS_PROVEEDORES, {
            title: "Proveedor",
            to: ROUTER_PATHS.compras.proveedores_nav,
            level: 2,
          }),
          /*  renderByModule(SystemModulesEnum.COMPRAS_COMPRAS, {
             title: "Compras",
             to: ROUTER_PATHS.compras.compras_nav,
             level: 2,
           }), */
          renderByPermission(PermissionsEnum.api_view_tipo_compras, {
            title: "Tipo compras",
            to: ROUTER_PATHS.compras.tipo_compras,
            level: 2,
          }),
          renderByPermission(PermissionsEnum.api_view_grupo_proveedores, {
            title: "Grupo proveedores",
            to: ROUTER_PATHS.compras.proveedores,
            level: 2,
          }),

          renderByModule(SystemModulesEnum.COMPRAS_PROVEEDORES, {
            title: "Revisión de Solicitud de Compra",
            to: ROUTER_PATHS.compras.solicitud_ingreso_revision_nav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.COMPRAS_ORDEN_COMPRA, {
            title: "Órdenes de Pedido",
            to: ROUTER_PATHS.compras.orden_compras_nav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.COMPRAS_ORDEN_COMPRA_CONSULTAS, {
            //title: "Consultas de Órdenes Finalizadas",
            title: "Reporte de Ingreso de Recepción",
            to: ROUTER_PATHS.compras.orden_compras_consulta_nav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.REPORTE_COMPRA, {
            title: "Reporte Compras",
            to: ROUTER_PATHS.compras.reporte_compra,
            level: 2,
          }),
        ].filter((item) => !!item),
      },
      {
        title: "Tickets de usuarios",
        icon: (
          <SvgIcon fontSize="small">
            <ShoppingCartIcon />
          </SvgIcon>
        ),
        level: NestedMenuItemLevels.firstLevelWithChildren,
        items: [].filter((item) => !!item) as NestedMenuItem[],
      },
      {
        title: "Activos",
        icon: (
          <SvgIcon fontSize="small">
            <FunnelIcon />
          </SvgIcon>
        ),
        level: NestedMenuItemLevels.firstLevelWithChildren,
        items: [
          renderByModule(SystemModulesEnum.ACTIVOS_ASIGNACION_ACTIVOS, {
            //TODO: Cambiar por permiso de contabilidad
            title: "Asignación activos",
            to: ROUTER_PATHS.activos.asignaciones_nav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.ACTIVOS_GRUPO_ACTIVOS, {
            //TODO: Cambiar por permiso de contabilidad
            title: "Grupo activos",
            to: ROUTER_PATHS.activos.grupo_activos_nav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.ACTIVOS_MANTENIMIENTO_ACTIVOS, {
            //TODO: Cambiar por permiso de contabilidad
            title: "Mantenimiento activos",
            to: ROUTER_PATHS.activos.mantenimiento_nav,
            level: 2,
          }),
        ].filter((item) => !!item),
      },
      {
        title: "Colaboradores",
        icon: (
          <SvgIcon fontSize="small">
            <ClipboardDocumentListIcon />
          </SvgIcon>
        ),
        level: NestedMenuItemLevels.firstLevelWithChildren,
        items: [
          renderByModule(SystemModulesEnum.NOMINA_CARGOS, {
            title: "Cargos",
            to: ROUTER_PATHS.nomina.cargos_nav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.NOMINA_EMPLEADOS, {
            title: "Empleados",
            to: ROUTER_PATHS.nomina.empleados_nav,
            level: 2,
          }),
        ].filter((item) => !!item),
      },

      ///* Contabilidad  --------------------------------
      {
        title: "Contabilidad",
        icon: (
          <SvgIcon fontSize="small">
            <CalculatorIcon />
          </SvgIcon>
        ),
        level: NestedMenuItemLevels.firstLevelWithChildren,
        items: [
          renderByModule(SystemModulesEnum.CONTABILIDAD_REGISTRO_FACTURA, {
            //TODO: Cambiar por permiso de contabilidad
            title: "Registro de Factura",
            to: ROUTER_PATHS.contabilidad.registroFacturaNav,
            level: 2,
          }),
          renderByModule(
            SystemModulesEnum.CONTABILIDAD_CLIENTES_COORPORATIVOS,
            {
              title: "Clientes Corporativos",
              to: ROUTER_PATHS.contabilidad.clientesCoorporativosNav,
              level: 2,
            },
          ),

          renderByModule(
            SystemModulesEnum.CONTABILIDAD_COMPROBANTE_PAGO_INSTALACION,
            {
              //TODO: Cambiar por permiso de contabilidad
              title: "Comprobantes de Pago Instalación",
              to: ROUTER_PATHS.contabilidad.revisionVoucherNav,
              level: 2,
            },
          ),

          renderByModule(SystemModulesEnum.CONTABILIDAD_PLAN_DE_CUENTAS, {
            //TODO: Cambiar por permiso de contabilidad
            title: "Plan de Cuenta",
            to: ROUTER_PATHS.contabilidad.planCuentaNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.INVENTARIO_TOMA_FISICA, {
            ///* TODO: Cambiar permisos
            title: "Toma Fisica",
            to: ROUTER_PATHS.inventario.tomaFisicaHistorialNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CONTABILIDAD_GESTION_INVENTARIO_CUADRE, { // TODO: Cambiar por permiso de contabilida
            ///* TODO: Cambiar permisos
            title: "Gestión Inventario Cuadre",
            to: ROUTER_PATHS.contabilidad.gestionInventarioCuadreNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CONTABILIDAD_REINGRESO_SERIE, { // TODO: Cambiar por permiso de contabilidad
            ///* TODO: Cambiar permisos
            title: "Reingreso de Serie",
            to: ROUTER_PATHS.contabilidad.reingresoSerieNav,
            level: 2,
          }),
        ].filter((item) => !!item),
      },

      ///* Soporte Técnico  --------------------------------
      {
        title: "Soporte Técnico",
        icon: (
          <SvgIcon fontSize="small">
            <SquaresPlusIcon />
          </SvgIcon>
        ),
        level: NestedMenuItemLevels.firstLevelWithChildren,
        items: [
          renderByModule(
            SystemModulesEnum.SOPORTE_TECNICO_DESBLOQUEO_USUARIOS,
            {
              title: "Desbloqueo de Usuarios",
              to: ROUTER_PATHS.soporteTecnico.desbloqueoUsuarioNav,
              level: 2,
            },
          ),
          renderByModule(
            SystemModulesEnum.SOPORTE_TECNICO_TIKETS_USUARIOS_REVISAR,
            {
              title: "Administrar tickets de usuarios",
              to: ROUTER_PATHS.tiketUser.tikets_usuarios_nav,
              level: 2,
            },
          ),
        ].filter((item) => !!item),
      },
      //**marketing
      {
        title: "Marketing",
        icon: (
          <SvgIcon fontSize="small">
            <ChartBarIcon />
          </SvgIcon>
        ),
        level: NestedMenuItemLevels.firstLevelWithChildren,
        items: [
          renderByModule(SystemModulesEnum.MARKETING_SOLICITUDES, {
            title: "Solicitudes Evento/Canje",
            to: ROUTER_PATHS.marketing.solicitudesCanjeEventoNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.MARKETING_REVISION_SOLICITUDES, {
            title: "Revisión Solicitudes Evento/Canje",
            to: ROUTER_PATHS.marketing.revisarSolicitudesCanjeEventoNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.MARKETING_SOLUCION_SOLICITUDES, {
            title: "Solución a Solicitudes Evento/Canje",
            to: ROUTER_PATHS.marketing.solucionSolicitudesCanjeEventoNav,
            level: 2,
          }),
        ].filter((item) => !!item),
      },
      {
        title: "Campañas",
        level: NestedMenuItemLevels.firstLevelWithChildren,
        icon: (
          <SvgIcon fontSize="small">
            <MegaphoneIcon />
          </SvgIcon>
        ),
        items: [
          renderByModule(SystemModulesEnum.CAMPANAS_CAMPANAS, {
            title: "Migración / Fidelización",
            to: "/campanas",
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CAMPANAS_COBRANZAS, {
            title: "Cobranzas",
            to: "/campanas/cobranzas",
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CAMPANAS_FIRMA_ADENDUM, {
            title: "Firma Adéndum",
            to: "/campanas/firma-adendum",
            level: 2,
          }),
          renderByModule(SystemModulesEnum.CAMPANAS_FIDELIZACION, {
            title: "Campañas de Fidelización",
            to: "/campanas/fidelizacion-campana",
            level: 2,
          }),
        ].filter((item) => !!item),
      },

      {
        title: "Soluciones Digitales",
        icon: (
          <SvgIcon fontSize="small">
            <PrecisionManufacturing />
          </SvgIcon>
        ),
        level: NestedMenuItemLevels.firstLevelWithChildren,
        items: [
          renderByModule(SystemModulesEnum.SOLUCIONES_DIGITALES_SERVICIOS, {
            title: "Servicios Digitales",
            to: ROUTER_PATHS.solucionesDigitales.servicios_nav,
            level: 2,
          }),
        ].filter((item) => !!item),
      },
      {
        title: "Reportes",
        icon: (
          <SvgIcon fontSize="small">
            <Assessment />
          </SvgIcon>
        ),
        level: NestedMenuItemLevels.firstLevelWithChildren,
        items: [
          renderByModule(SystemModulesEnum.REPORTES_SOFTNET, {
            title: "Reporte de Venta General",
            to: ROUTER_PATHS.supervisionVentas.reporteVentaNav,
            level: 2,
          }),
          renderByModule(
            SystemModulesEnum.REPORTES_SOFTNET_VENDEDORES_EXTERNOS_CALL_CENTER,
            {
              title: "Reporte de Vendedores Externos / Call Center",
              to: ROUTER_PATHS.supervisionVentas.reporteVendedoresExternosNav,
              level: 2,
            },
          ),
          renderByModule(
            SystemModulesEnum.REPORTES_SOFTNET_EQUIPOS_SIN_MOVIMIENTO,
            {
              title: "Reporte de Equipos Sin Movimiento",
              to: ROUTER_PATHS.supervisionVentas.reporteEquiposSinMovimientoNav,
              level: 2,
            },
          ),
          renderByModule(
            SystemModulesEnum.REPORTES_SOFTNET_CONTRATOS_PENDIENTES,
            {
              title: "Reporte de Contratos Pendientes",
              to: ROUTER_PATHS.supervisionVentas.reporteContratosPendientesNav,
              level: 2,
            },
          ),
          renderByModule(SystemModulesEnum.REPORTES_SOFTNET_COMISIONES, {
            title: "Reporte de Comisiones",
            to: ROUTER_PATHS.supervisionVentas.reporteComisionesNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.REPORTES_SOFTNET_VALORES, {
            title: "Reporte de Valores",
            to: ROUTER_PATHS.supervisionVentas.reporteClientesValoresSoftnetNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.REPORTES_SOFTNET_CONTRATOS, {
            title: "Reporte de Contratos",
            to: ROUTER_PATHS.supervisionVentas.reporteContratosSoftnetNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.REPORTES_SOFTNET_CONTRATOS, {
            title: "Reporte de Clientes Golondrinas",
            to: ROUTER_PATHS.supervisionVentas.reporteContratosGolondrinasNav,
            level: 2,
          }),
          renderByModule(SystemModulesEnum.REPORTES_ACTIVIDAD_INVENTARIO,
            {
              title: "Reporte de Actividad de Inventario",
              to: ROUTER_PATHS.reportes.reporteActividadInventarioNav,
              level: 2,
            }
          ),
        ].filter((item) => !!item),
      },
      ///* Soporte Técnico  --------------------------------
      {
        title: "Tickets Soporte Clientes ",
        icon: (
          <SvgIcon fontSize="small">
            <SupportAgent />
          </SvgIcon>
        ),
        level: NestedMenuItemLevels.firstLevelWithChildren,
        items: [
          renderByModule(
            SystemModulesEnum.NUEVO_TICKET_TICKETS_CLIENTES,
            {
              title: "Tickets Clientes",
              to: ROUTER_PATHS.ticketSoporteCliente.nuevo_ticket_nav,
              level: 2,
            }
          ),
          renderByModule(
            SystemModulesEnum.CATEGORIA_TICKETS_CLIENTES,
            {
              title: "Categoria Incidentes",
              to: ROUTER_PATHS.ticketSoporteCliente.categoria_incidente_nav,
              level: 2,
            }
          ),
          renderByModule(
            SystemModulesEnum.SUBCATEGORIA_TICKETS_CLIENTES,
            {
              title: "Subcategoria Incidentes",
              to: ROUTER_PATHS.ticketSoporteCliente.subcategoria_incidente_nav,
              level: 2,
            }
          ),
          renderByModule(
            SystemModulesEnum.CAUSAL_TICKETS_CLIENTES,
            {
              title: "Causal Incidentes",
              to: ROUTER_PATHS.ticketSoporteCliente.causal_incidente_nav,
              level: 2,
            }
          ),
          renderByModule(
            SystemModulesEnum.MIS_TICKETS,
            {
              title: "Mis Tickets",
              to: ROUTER_PATHS.ticketSoporteCliente.mis_tickets_nav,
              level: 2
            }
          ),
          // TODO: Pruebas
          /* SystemModulesEnum.MIS_TICKETS,
            {
              title: "Mis Tickets",
              to: ROUTER_PATHS.ticketSoporteCliente.mis_tickets_nav,
              level: 2
            }, */
          renderByModule(
            SystemModulesEnum.TICKETS_MASIVOS,
            {
              title: "Daños Masivos",
              to: ROUTER_PATHS.ticketSoporteCliente.masivos_nav,
              level: 2,
            }
          ),
          renderByModule(
            SystemModulesEnum.TICKETS_AGENDA_TECNICOS,
            {
              title: "Agenda Tecnicos",
              to: ROUTER_PATHS.ticketSoporteCliente.agenda_nav,
              level: 2,
            }
          ),
          renderByModule(
            SystemModulesEnum.TICKETS_CATALOGOS,
            {
              title: "Catalogos Tickets",
              to: ROUTER_PATHS.ticketSoporteCliente.catalogos_nav,
              level: 2,
            }
          ),
          renderByModule(
            SystemModulesEnum.TICKETS_CATALOGOS,
            {
              title: "Migración Softnet",
              to: ROUTER_PATHS.ticketSoporteCliente.migracion_softnet_nav,
              level: 2,
            }
          ),
          renderByModule(
            SystemModulesEnum.TICKETS_INTERNOS,
            {
              title: "Tickets Internos",
              to: ROUTER_PATHS.ticketSoporteCliente.tickets_internos_nav,
              level: 2,
            }
          ),
        ].filter((item) => !!item),
      },
      ///**Vista */
      {
        title: "Agendamiento Visitas",
        icon: (
          <SvgIcon fontSize="small">
            <CalendarDaysIcon />
          </SvgIcon>
        ),
        level: NestedMenuItemLevels.firstLevelWithChildren,
        items: [
          renderByModule(
            SystemModulesEnum.TICKETS_PENDIENTES_ASIGNACION,
            {
              title: "Tickets Pendientes Asignación",
              to: ROUTER_PATHS.visitas.ticket_nav,
              level: 2
            }
          ),
          renderByModule(
            SystemModulesEnum.VISITAS_AGENDA_DIARIA,
            {
              title: "Agenda Vista Diaria",
              to: ROUTER_PATHS.visitas.agenda_diaria_nav,
              level: 2
            }
          )
        ].filter((item) => !!item),
      }
      /* ///* Contratos
      {
        title: 'Contrato',
        icon: (
          <SvgIcon fontSize="small">
            <FolderMinusIcon />
          </SvgIcon>
        ),
        level: NestedMenuItemLevels.firstLevelWithChildren,
        url: '',
        items: [
          renderByModule(
            SystemModulesEnum.SOPORTE_TECNICO_DESBLOQUEO_USUARIOS,
            {
              title: 'Contrato',
              to: ROUTER_PATHS.soporteTecnico.desbloqueoUsuarioNav,
              level: 2,
            }
          )
        ].filter(item => !!item) as NestedMenuItem[],
      } */
    ];
  }, [renderByPermission, renderByModule]);

  const cleanedMenu = cleanEmptyItems(nestedMenuItesm);
  return { nestedMenuItesm: cleanedMenu };
};
